import { Alert, Box, Typography } from "@mui/material";
import { useMemo, useCallback, useState } from "react";
import { useHierarchyContext } from "../contexts/HierarchyContext";
import { TimeRangeFilterBar } from "../components/filters/TimeRangeFilterBar";
import { RefreshControls } from "../components/filters/RefreshControls";
import { FilterBar } from "../components/filters/FilterBar";
import { Panel } from "../components/layout/Panel";
import type { LogRecord } from "../api/client";
import { useTimeRangeFromQuery, type SeverityRange } from "../hooks/useRouteParams";
import { CompGrid, type Column, type ColumnFilterProps, type FilterRenderProps } from "../components/grid/CompGrid";
import { createCommonLogColumns } from "../components/grid/columns/logColumns";
import { createTraceIdColumn } from "../components/grid/columns/traceColumns";
import { useMetadata } from "../hooks/useMetadata";
import { useLogs } from "../hooks/useLogs";
import { useGridFilters } from "../hooks/useGridFilters";
import { PageErrorState } from "../components/pages/PageErrorState";
import { PageLoadingState } from "../components/pages/PageLoadingState";
import { SpanIdLink } from "../components/links/IdLink";
import { formatMetadataForFilters } from "../utils/metadata";
import {
  GridIdsFilterPopover,
  GridCheckboxSearchFilterPopover,
  GridSeverityFilterPopover,
  GridMessageFilterPopover,
} from "../components/filters/grid";
import type { GridIdsFilterPopoverProps } from "../components/filters/grid/GridIdsFilterPopover";
import type { GridCheckboxSearchFilterPopoverProps } from "../components/filters/grid/GridCheckboxSearchFilterPopover";

export function PageLogsQuery() {
  const { hierarchy } = useHierarchyContext();
  const serviceSlug = hierarchy?.service?.slug ?? null;
  const { range } = useTimeRangeFromQuery();

  const { filters, setFilters } = useGridFilters(serviceSlug, "logs");

  const { metadata } = useMetadata(serviceSlug);
  const metadataFormatted = useMemo(() => {
    return formatMetadataForFilters(metadata);
  }, [metadata]);

  const [ refreshToken, setRefreshToken ] = useState(0);

  const { logs, loading, error, isFetching } = useLogs({
    serviceSlug,
    timeRange: range,
    serviceNames: filters.serviceNames,
    serviceInstances: filters.serviceInstances,
    traceIds: filters.traceIds,
    spanIds: filters.spanIds,
    logIds: filters.logIds,
    severityRanges: filters.severityRanges,
    bodyFilters: filters.bodyFilters,
    sort: filters.sort,
    order: filters.order,
    enabled: !!serviceSlug,
    limit: 500,
    refreshToken,
  });

  const handleSortChange = useCallback(
    (columnId: string, direction: "asc" | "desc") => {
      const sortMap: Record<string, "ts" | "severity_text"> = {
        timestamp: "ts",
        ts: "ts",
        severity: "severity_text",
        severity_text: "severity_text",
      };
      const sort = sortMap[columnId] ?? "ts";
      setFilters((prev) => ({ ...prev, sort, order: direction }));
    },
    [setFilters],
  );

  const columnFilterProps = useMemo((): Record<string, ColumnFilterProps> => {
    return {
      trace_id: {
        value: filters.traceIds,
        onChange: (v) => setFilters((prev) => ({ ...prev, traceIds: (v as string[]) ?? [] })),
        hasActive: filters.traceIds.length > 0,
      },
      span_id: {
        value: filters.spanIds,
        onChange: (v) => setFilters((prev) => ({ ...prev, spanIds: (v as string[]) ?? [] })),
        hasActive: filters.spanIds.length > 0,
      },
      log_id: {
        value: filters.logIds,
        onChange: (v) => setFilters((prev) => ({ ...prev, logIds: (v as string[]) ?? [] })),
        hasActive: filters.logIds.length > 0,
      },
      body: {
        value: filters.bodyFilters,
        onChange: (v) =>
          setFilters((prev) => ({ ...prev, bodyFilters: (v as typeof prev.bodyFilters) ?? [] })),
        hasActive: filters.bodyFilters.length > 0,
      },
      severity: {
        value: filters.severityRanges,
        onChange: (v) =>
          setFilters((prev) => ({
            ...prev,
            severityRanges: (v as string[]) ?? [],
          })),
        hasActive: filters.severityRanges.length > 0,
      },
      service_name: {
        value: filters.serviceNames,
        onChange: (v) =>
          setFilters((prev) => ({ ...prev, serviceNames: (v as string[]) ?? [] })),
        hasActive: filters.serviceNames.length > 0,
      },
      service_instance: {
        value: filters.serviceInstances,
        onChange: (v) =>
          setFilters((prev) => ({ ...prev, serviceInstances: (v as string[]) ?? [] })),
        hasActive: filters.serviceInstances.length > 0,
      },
    };
  }, [filters, setFilters]);

  const baseColumns = useMemo(
    () => createCommonLogColumns({ serviceSlug }),
    [serviceSlug],
  );

  const columns: Column<LogRecord>[] = useMemo(() => {
    const cols = baseColumns.map((col) => {
      if (col.id === "timestamp") {
        return { ...col, sortKey: "ts" as const };
      }
      if (col.id === "body") {
        return {
          ...col,
          sortKey: "ts" as const,
          filterable: true,
          filterRender: (props: FilterRenderProps & { column: Column<LogRecord> }) => (
            <GridMessageFilterPopover {...props} label="Message" />
          ),
        };
      }
      if (col.id === "severity") {
        return {
          ...col,
          sortKey: "severity_text" as const,
          filterable: true,
          filterRender: (props: FilterRenderProps & { column: Column<LogRecord> }) => (
            <GridSeverityFilterPopover {...props} />
          ),
        };
      }
      if (col.id === "service") {
        return col;
      }
      if (col.id === "log_id") {
        return {
          ...col,
          filterable: true,
          filterRender: (props: FilterRenderProps & { column: Column<LogRecord> }) => (
            <GridIdsFilterPopover
              {...props}
              label="Log IDs"
              placeholder="Add log ID (UUID)"
            />
          ),
        };
      }
      return col;
    });

    const traceCol = {
      ...createTraceIdColumn<LogRecord>({
        serviceSlug: serviceSlug ?? "",
        getTraceId: (log) => log.trace_id,
        truncateAt: 16,
      }),
      sortKey: "ts" as const,
      filterable: true,
      filterRender: (props: FilterRenderProps & { column: Column<LogRecord> }) => (
        <GridIdsFilterPopover {...props} label="Trace IDs" placeholder="Add trace ID" />
      ),
    };

    const spanCol: Column<LogRecord> = {
      id: "span_id",
      label: "Span ID",
      sortable: true,
      sortKey: "ts",
      sortValue: (log) => log.span_id || "",
      render: (log) => (
        <SpanIdLink serviceSlug={serviceSlug} spanId={log.span_id} />
      ),
      filterable: true,
      filterRender: (props: FilterRenderProps & { column: Column<LogRecord> }) => (
        <GridIdsFilterPopover {...props} label="Span IDs" placeholder="Add span ID" />
      ),
    };

    const serviceNameCol: Column<LogRecord> = {
      id: "service_name",
      label: "Service Name",
      sortable: true,
      sortValue: (log) => log.service_name,
      render: (log) => <Typography variant="body2">{log.service_name}</Typography>,
      hiddenByDefault: true,
      filterable: true,
      filterRender: (props: FilterRenderProps & { column: Column<LogRecord> }) => (
        <GridCheckboxSearchFilterPopover
          {...props}
          label="Service Names"
          options={metadataFormatted.services}
        />
      ),
    };

    const serviceInstanceCol: Column<LogRecord> = {
      id: "service_instance",
      label: "Instance",
      sortable: true,
      sortValue: (log) => log.service_instance ?? "",
      render: (log) => (
        <Typography variant="body2">{log.service_instance ?? "—"}</Typography>
      ),
      hiddenByDefault: true,
      filterable: true,
      filterRender: (props: FilterRenderProps & { column: Column<LogRecord> }) => (
        <GridCheckboxSearchFilterPopover
          {...props}
          label="Service Instances"
          options={metadataFormatted.instances}
        />
      ),
    };

    return [...cols, traceCol, spanCol, serviceNameCol, serviceInstanceCol];
  }, [baseColumns, serviceSlug, metadataFormatted.services, metadataFormatted.instances]);

  const sortColumnMap: Record<string, string> = {
    ts: "timestamp",
    severity_text: "severity",
  };
  const sortColumnId =
    Object.entries(sortColumnMap).find(([k]) => k === filters.sort)?.[1] ?? "timestamp";

  if (!serviceSlug) {
    return (
      <Box>
        <Alert severity="warning">
          No Service in URL. Please navigate from a product's services list.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
      <Panel>
        <FilterBar>
          <TimeRangeFilterBar />
          <Box data-filterbar-wide="true" sx={{ maxWidth: "none", minWidth: "auto" }}>
            <RefreshControls setRefreshToken={setRefreshToken} isFetching={isFetching} />
          </Box>
        </FilterBar>
        {error ? <PageErrorState message={error} sx={{ mt: 1.5 }} /> : null}
        {loading ? <PageLoadingState message="Loading logs..." sx={{ mt: 1.5 }} /> : null}
      </Panel>

      <CompGrid
        items={logs}
        loading={loading}
        error={error}
        emptyMessage="No logs found for the selected filters"
        keyExtractor={(log) => log.log_id}
        columns={columns}
        visibleColumns={filters.visibleColumns}
        onVisibleColumnsChange={(ids) =>
          setFilters((prev) => ({ ...prev, visibleColumns: ids }))
        }
        sortColumn={sortColumnId}
        sortDirection={filters.order}
        onSortChange={handleSortChange}
        serverSort
        columnFilterProps={columnFilterProps}
      />
    </Box>
  );
}
